/**
 * 과제 커버 WebP: 최초 fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * `path|createdAt` 버전이 바뀌면 다시 받아 갱신한다.
 */

import type { AssignmentCoverImage } from '../types/assignmentPointsNote';

const DB_NAME = 'ClassPlannerAssignmentCoverCache';
const DB_VERSION = 1;
const STORE = 'images';

type CoverCacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryById = new Map<string, MemoryEntry>();
const inflightById = new Map<string, Promise<string | null>>();

export function assignmentCoverCacheVersion(
  image: Pick<AssignmentCoverImage, 'path' | 'createdAt'>
): string {
  return `${image.path.trim()}|${image.createdAt.trim()}`;
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<CoverCacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<CoverCacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as CoverCacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: CoverCacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function idbDelete(key: string): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).delete(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchImageArrayBuffer(
  sourceUrl: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    const mime =
      res.headers.get('content-type')?.split(';')[0]?.trim() || 'image/webp';
    return { buffer, mime };
  } catch {
    return null;
  }
}

function remember(id: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryById.get(id);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryById.set(id, { blobUrl, version });
  return blobUrl;
}

/** 동기 peek — 메모리에 같은 버전이 있으면 즉시 반환 */
export function peekCachedAssignmentCoverUrl(
  image: Pick<AssignmentCoverImage, 'id' | 'path' | 'createdAt'>
): string | null {
  const id = image.id.trim();
  if (!id) return null;
  const version = assignmentCoverCacheVersion(image);
  const mem = memoryById.get(id);
  if (mem && mem.version === version) return mem.blobUrl;
  return null;
}

/**
 * @returns Blob URL — 호출 측에서 revoke 하지 말 것(세션 동안 재사용).
 * 버전이 다르면 네트워크에서 다시 받아 IDB·메모리를 갱신한다.
 * `allowNetwork: false`이면 메모리·IndexedDB만 보고 fetch 하지 않는다.
 */
export async function getCachedAssignmentCoverUrl(
  image: Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'>,
  options?: { allowNetwork?: boolean }
): Promise<string | null> {
  const id = image.id.trim();
  const sourceUrl = image.url.trim();
  const allowNetwork = options?.allowNetwork !== false;
  if (!id) return null;
  if (allowNetwork && !sourceUrl) return null;
  const version = assignmentCoverCacheVersion(image);

  const mem = memoryById.get(id);
  if (mem && mem.version === version) return mem.blobUrl;

  const inflightKey = `${id}:${allowNetwork ? 'net' : 'cache'}`;
  let inflight = inflightById.get(inflightKey);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(id);
        if (
          cached &&
          cached.buffer?.byteLength > 0 &&
          cached.version === version
        ) {
          return remember(id, version, cached.buffer, cached.mime || 'image/webp');
        }
        if (!allowNetwork && cached?.buffer?.byteLength) {
          return remember(id, cached.version, cached.buffer, cached.mime || 'image/webp');
        }
      } catch {
        /* IDB 미지원·거부 */
      }

      if (!allowNetwork) return null;

      const got = await fetchImageArrayBuffer(sourceUrl);
      if (!got) {
        /* 네트워크 실패 시 구버전 캐시라도 있으면 표시 */
        try {
          const stale = await idbGet(id);
          if (stale?.buffer?.byteLength) {
            return remember(id, stale.version, stale.buffer, stale.mime || 'image/webp');
          }
        } catch {
          /* ignore */
        }
        return null;
      }

      try {
        await idbPut(id, {
          buffer: got.buffer,
          version,
          mime: got.mime,
        });
      } catch {
        /* 저장 실패해도 표시는 가능 */
      }
      return remember(id, version, got.buffer, got.mime);
    })().finally(() => {
      inflightById.delete(inflightKey);
    });
    inflightById.set(inflightKey, inflight);
  }
  return inflight;
}

/** 업로드 직후 로컬 blob을 캐시에 넣어 재 fetch를 생략 */
export async function putAssignmentCoverCacheFromBlob(
  image: Pick<AssignmentCoverImage, 'id' | 'path' | 'createdAt'>,
  blob: Blob
): Promise<string | null> {
  const id = image.id.trim();
  if (!id || blob.size <= 0) return null;
  const version = assignmentCoverCacheVersion(image);
  const mime = blob.type || 'image/webp';
  const buffer = await blob.arrayBuffer();
  try {
    await idbPut(id, { buffer, version, mime });
  } catch {
    /* ignore */
  }
  return remember(id, version, buffer, mime);
}

export async function invalidateAssignmentCoverCache(imageId: string): Promise<void> {
  const id = imageId.trim();
  if (!id) return;
  const mem = memoryById.get(id);
  if (mem) {
    try {
      URL.revokeObjectURL(mem.blobUrl);
    } catch {
      /* ignore */
    }
    memoryById.delete(id);
  }
  inflightById.delete(`${id}:net`);
  inflightById.delete(`${id}:cache`);
  try {
    await idbDelete(id);
  } catch {
    /* ignore */
  }
}

/** 백그라운드 프리로드 */
export function preloadAssignmentCoverImages(
  images: Array<Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'> | null | undefined>
): void {
  const list = images.filter(
    (i): i is Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'> =>
      Boolean(i?.id?.trim() && i?.url?.trim())
  );
  void Promise.all(list.map((img) => getCachedAssignmentCoverUrl(img)));
}

/** IndexedDB·메모리만 워밍 — 네트워크 없음 */
export function preloadAssignmentCoverImagesFromCache(
  images: Array<Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'> | null | undefined>
): void {
  const list = images.filter(
    (i): i is Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'> =>
      Boolean(i?.id?.trim())
  );
  void Promise.all(list.map((img) => getCachedAssignmentCoverUrl(img, { allowNetwork: false })));
}
